// src/components/frontpage/SuggestionReviewQueue.jsx
// Review queue for pending directory suggestions. Shown to Stewards and platform admins.
// Data layer: getReviewableSuggestions, approveSuggestion, rejectSuggestion from directoryService.
// The queue holds every pending suggestion this person may decide, wherever it was filed
// (Roadmap #87, R6) — the same rule approve and reject apply, so nothing shown here is refused.
// Gate: RLS is the real gate; this component is only rendered when the client-side
// isSteward/isPlatformAdmin check passes (see SidebarLocality).

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { getReviewableSuggestions, approveSuggestion, rejectSuggestion, getBoundaryNames } from '../../data/directoryService';
import { getPublicProfiles } from '../../data/profileService';
import ConfirmationModal from '../core/ConfirmationModal';
import { tokens } from '../../styles/designTokens';

// Payload field display order
const PAYLOAD_FIELDS = ['name', 'section', 'category', 'description', 'phone', 'email', 'website', 'address'];

const SuggestionReviewQueue = ({ onClose, onCountChange }) => {
  const { t, formattingLocale } = useTranslation('directory');

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');
  const [actionInProgress, setActionInProgress] = useState(null); // suggestion id being actioned
  const [confirmRejectId, setConfirmRejectId] = useState(null);
  const [userNames, setUserNames] = useState({}); // { uuid: display_name }
  const [listingAreaNames, setListingAreaNames] = useState({}); // { boundary_id: name }

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getReviewableSuggestions();
      setSuggestions(data);
      setLoading(false);

      // A correction is about the listing, so it names the listing's own area (as the directory
      // labels it), not the area it was filed from. Suggesters' names are fetched alongside.
      const listingAreas = data.filter(s => s.listing_id && !s.listing_is_national).map(s => s.listing_boundary_id);
      const userIds = [...new Set(data.map(s => s.suggested_by).filter(Boolean))];
      const [areaNames, { data: users }] = await Promise.all([
        listingAreas.length > 0 ? getBoundaryNames(listingAreas) : Promise.resolve({}),
        userIds.length > 0 ? getPublicProfiles(userIds) : Promise.resolve({ data: null })
      ]);
      setListingAreaNames(areaNames);
      if (users) {
        const names = {};
        users.forEach(u => { names[u.id] = u.display_name; });
        setUserNames(names);
      }
    } catch (err) {
      console.error('fetchSuggestions error:', err);
      setError(t('review_error'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Toast auto-clear
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
  };

  const handleApprove = async (suggestionId) => {
    setActionInProgress(suggestionId);
    const result = await approveSuggestion(suggestionId);
    setActionInProgress(null);

    if (result.success) {
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      showToast(t('review_approved'));
      if (onCountChange) onCountChange(-1);
      window.dispatchEvent(new CustomEvent('ournet:directory-changed'));
    } else {
      showToast(result.error?.message || t('review_error'), 'error');
    }
  };

  const handleReject = async (suggestionId) => {
    setConfirmRejectId(null);
    setActionInProgress(suggestionId);
    const result = await rejectSuggestion(suggestionId);
    setActionInProgress(null);

    if (result.success) {
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      showToast(t('review_rejected'));
      if (onCountChange) onCountChange(-1);
    } else {
      showToast(result.error?.message || t('review_error'), 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(formattingLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Close on ESC — but not while the reject confirmation is open: Escape there answers the
  // confirmation only (#78).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && confirmRejectId === null) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, confirmRejectId]);

  return (
    // Overlay tier, below the modal tier, so its own reject confirmation shows in front (#78).
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: tokens.zIndex.overlay,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: tokens.colors.backdrop
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'relative',
        background: 'white',
        borderRadius: '12px',
        width: '92%',
        maxWidth: '520px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        fontFamily: 'OpenSauceSans, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#024B99',
            margin: 0
          }}>
            {t('review_title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1
            }}
            aria-label={t('common:close')}
          >
            {'\u2715'}
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 24px 24px'
        }}>
          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '32px 0',
              color: '#6b7280',
              fontSize: '13px'
            }}>
              {t('common:loading')}
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              background: '#fef2f2',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && suggestions.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px 0',
              color: '#9ca3af',
              fontSize: '13px'
            }}>
              {t('review_empty')}
            </div>
          )}

          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              userName={userNames[suggestion.suggested_by]}
              areaName={suggestion.listing_id
                ? (suggestion.listing_is_national ? t('frontpage:dir_area_national') : listingAreaNames[suggestion.listing_boundary_id])
                : suggestion.area_name}
              formatDate={formatDate}
              onApprove={() => handleApprove(suggestion.id)}
              onReject={() => setConfirmRejectId(suggestion.id)}
              isActioning={actionInProgress === suggestion.id}
              t={t}
            />
          ))}
        </div>

        {/* Toast */}
        {toastMessage && (
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 16px',
            borderRadius: '8px',
            background: toastType === 'error' ? '#fef2f2' : '#f0fdf4',
            color: toastType === 'error' ? '#dc2626' : '#059669',
            fontSize: '12px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            whiteSpace: 'nowrap'
          }}>
            {toastMessage}
          </div>
        )}
      </div>

      {/* Reject confirmation modal */}
      <ConfirmationModal
        visible={confirmRejectId !== null}
        title={t('review_reject_confirm_title')}
        message={t('review_reject_confirm_message')}
        okLabel={t('review_reject')}
        cancelLabel={t('common:cancel')}
        danger={true}
        onOk={() => handleReject(confirmRejectId)}
        onCancel={() => setConfirmRejectId(null)}
      />
    </div>
  );
};

// ============================================================================
// SUGGESTION CARD
// ============================================================================

const SuggestionCard = ({ suggestion, userName, areaName, formatDate, onApprove, onReject, isActioning, t }) => {
  const isCorrection = Boolean(suggestion.listing_id);
  const payload = suggestion.payload || {};

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '12px',
      background: '#fafbfc'
    }}>
      {/* Type badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px'
      }}>
        <span style={{
          display: 'inline-block',
          padding: '3px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          background: isCorrection ? '#ede9fe' : '#dbeafe',
          color: isCorrection ? '#6d28d9' : '#1e40af'
        }}>
          {isCorrection ? t('review_correction') : t('review_new_listing')}
        </span>
        {/* The area this suggestion is about (data, not translated) */}
        {areaName && (
          <span style={{ fontSize: '11px', color: tokens.colors.textSecondary, marginLeft: '8px', marginRight: 'auto' }}>
            {areaName}
          </span>
        )}
        <span style={{
          fontSize: '11px',
          color: '#9ca3af'
        }}>
          {formatDate(suggestion.created_at)}
        </span>
      </div>

      {/* Suggester name */}
      {userName && (
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginBottom: '8px'
        }}>
          {t('review_suggested_by', { name: userName })}
        </div>
      )}

      {/* Payload fields */}
      <div style={{ marginBottom: suggestion.note ? '8px' : '12px' }}>
        {PAYLOAD_FIELDS.map(field => {
          const value = payload[field];
          if (!value) return null;
          return (
            <div key={field} style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '4px',
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              <span style={{
                color: '#6b7280',
                fontWeight: '600',
                minWidth: '70px',
                flexShrink: 0
              }}>
                {t(`review_field_${field}`)}
              </span>
              <span style={{ color: '#1f2937', wordBreak: 'break-word' }}>
                {value}
              </span>
            </div>
          );
        })}
      </div>

      {/* Note from suggester */}
      {suggestion.note && (
        <div style={{
          padding: '8px 10px',
          background: '#f3f4f6',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#4b5563',
          fontStyle: 'italic',
          marginBottom: '12px',
          lineHeight: '1.4'
        }}>
          {suggestion.note}
        </div>
      )}

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={onReject}
          disabled={isActioning}
          style={{
            padding: '6px 14px',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            background: 'white',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: '#dc2626',
            cursor: isActioning ? 'default' : 'pointer',
            opacity: isActioning ? 0.5 : 1
          }}
        >
          {t('review_reject')}
        </button>
        <button
          onClick={onApprove}
          disabled={isActioning}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderRadius: '6px',
            background: isActioning ? '#93c5fd' : '#024B99',
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            cursor: isActioning ? 'default' : 'pointer'
          }}
        >
          {isActioning ? t('common:loading') : t('review_approve')}
        </button>
      </div>
    </div>
  );
};

export default SuggestionReviewQueue;
